import React from 'react';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Skeleton } from '@mui/material';

import Currencies from 'arbitrip-common/general/utils/Currencies';

import CashWalletConstants, { maxWithdrawableNet } from './CashWalletConstants';
import { formatAmountWithoutSymbol } from './CashWalletMoney';
import InfoPopover from './InfoPopover.react';

const { BUTTON_STATE, DATE_FORMAT, DEFAULT_CURRENCY } = CashWalletConstants;

const SKELETON_GRADIENT = 'linear-gradient(90deg, #fbfbfb 0%, #ededed 51.923%, #efefef 100%)';
const SKELETON_BORDER_RADIUS = 6;

const WithdrawButton = ({ enabled, label, onClick }) => (
    <Button
        className="cash-wallet-balance-cta"
        variant="contained"
        color="primary"
        disabled={!enabled}
        onClick={enabled ? onClick : undefined}
    >
        {label}
    </Button>
);

// NET-vs-NET: the threshold is an ex-VAT figure, and so is the balance the agent is shown above.
const ThresholdHelper = ({ t, threshold, netBalance, currency }) => {
    const remaining = Currencies.getPriceWithCurrency(threshold - netBalance, currency);
    const thresholdDisplay = Currencies.getPriceWithCurrency(threshold, currency);

    return (
        <div className="cash-wallet-helper">
            <span className="cash-wallet-helper-highlight">{t('Helper.MoreToGoAmount', { amount: remaining })}</span>
            <span>{t('Helper.MoreToGoTail')}</span>
            <InfoPopover title={t('Popover.PayoutMinimumTitle')}>
                <Trans
                    t={t}
                    i18nKey="Popover.PayoutMinimumBody"
                    values={{ threshold: thresholdDisplay }}
                    components={[<strong key="0" className="cash-wallet-info-popover-strong" />]}
                />
            </InfoPopover>
        </div>
    );
};

const PayoutTimingHelper = ({ t, expectedPayout }) => {
    const payoutDate = expectedPayout ? moment(expectedPayout).format(DATE_FORMAT) : '';

    return (
        <div className="cash-wallet-helper">
            <span>{t('Helper.PayoutPrefix')}</span>
            <span className="cash-wallet-helper-highlight">{t('Helper.PayoutToday')}</span>
            <span>{t('Helper.PayoutMiddle')}</span>
            <span className="cash-wallet-helper-highlight">{payoutDate}</span>
            <InfoPopover title={t('Popover.PayoutTimingTitle')}>
                <div className="cash-wallet-info-popover-paragraphs">
                    <div>
                        <Trans
                            t={t}
                            i18nKey="Popover.PayoutTimingLine1"
                            components={[<strong key="0" className="cash-wallet-info-popover-strong" />]}
                        />
                    </div>
                    <div>
                        <Trans
                            t={t}
                            i18nKey="Popover.PayoutTimingLine2"
                            components={[<strong key="0" className="cash-wallet-info-popover-strong" />]}
                        />
                    </div>
                </div>
            </InfoPopover>
        </div>
    );
};

const BalanceHeader = ({ loading, dashboard, onRequestWithdrawal }) => {
    const { t } = useTranslation('CashWallet');

    if (loading) {
        return (
            <div className="cash-wallet-balance-header">
                <div className="cash-wallet-balance-info">
                    <Skeleton
                        variant="rounded"
                        width={110}
                        height={53}
                        sx={{ borderRadius: `${SKELETON_BORDER_RADIUS}px`, background: SKELETON_GRADIENT }}
                    />
                    <Skeleton
                        variant="rounded"
                        width={155}
                        height={21}
                        sx={{ borderRadius: `${SKELETON_BORDER_RADIUS}px`, background: SKELETON_GRADIENT }}
                    />
                </div>
                <div className="cash-wallet-balance-actions cash-wallet-balance-actions-skeleton">
                    <Skeleton
                        variant="rounded"
                        width={196}
                        height={53}
                        sx={{ borderRadius: `${SKELETON_BORDER_RADIUS}px`, background: SKELETON_GRADIENT }}
                    />
                    <Skeleton
                        variant="rounded"
                        width={390}
                        height={21}
                        sx={{ borderRadius: `${SKELETON_BORDER_RADIUS}px`, background: SKELETON_GRADIENT }}
                    />
                </div>
            </div>
        );
    }

    const currency = dashboard?.currency || DEFAULT_CURRENCY;
    const balance = Number.isFinite(dashboard?.balance) ? dashboard.balance : 0;
    // Deriving from the gross balance is only a fallback for a payload predating `net_balance`; both
    // paths call the same helper, so they agree exactly.
    const netBalance = Number.isFinite(dashboard?.net_balance)
        ? dashboard.net_balance
        : maxWithdrawableNet(balance);
    const threshold = dashboard?.threshold;
    const code = dashboard?.button_state?.code;

    const enabled = code === BUTTON_STATE.ENABLED;
    const isNegative = netBalance < 0;

    return (
        <div className="cash-wallet-balance-header">
            <div className="cash-wallet-balance-info">
                <div className="cash-wallet-balance-amount-wrap">
                    <span className="cash-wallet-balance-currency-symbol">{Currencies.getSymbol(currency)}</span>
                    <span className="cash-wallet-balance-amount">{formatAmountWithoutSymbol(netBalance)}</span>
                    {isNegative && code === BUTTON_STATE.ZERO_BALANCE && (
                        <InfoPopover title={t('Popover.NegativeBalanceTitle')}>
                            {t('Popover.NegativeBalanceBody')}
                        </InfoPopover>
                    )}
                </div>
                <div className="cash-wallet-balance-caption">
                    <div className="cash-wallet-balance-label">{t('Balance.AvailableBalance')}</div>
                    <div className="cash-wallet-balance-vat">{t('Balance.ExcludingVat')}</div>
                </div>
            </div>

            <div className="cash-wallet-balance-actions">
                <WithdrawButton enabled={enabled} label={t('Cta.RequestWithdrawal')} onClick={onRequestWithdrawal} />

                {code === BUTTON_STATE.ZERO_BALANCE && !isNegative && (
                    <div className="cash-wallet-helper">
                        <span>
                            {t('Helper.ZeroThreshold', {
                                threshold: Currencies.getPriceWithCurrency(threshold, currency),
                            })}
                        </span>
                    </div>
                )}

                {code === BUTTON_STATE.BELOW_THRESHOLD && (
                    <ThresholdHelper t={t} threshold={threshold} netBalance={netBalance} currency={currency} />
                )}

                {code === BUTTON_STATE.ENABLED && (
                    <PayoutTimingHelper t={t} expectedPayout={dashboard?.expected_payout} />
                )}

                {code === BUTTON_STATE.ACTIVE_REQUEST && (
                    <div className="cash-wallet-helper">
                        <span>{t('Helper.ActiveRequestPending')}</span>
                    </div>
                )}

                {code === BUTTON_STATE.MONTHLY_CAP_REACHED && (
                    <div className="cash-wallet-helper">
                        <span>{t('Helper.MonthlyCapReached')}</span>
                    </div>
                )}

                {code === BUTTON_STATE.FROZEN && (
                    <div className="cash-wallet-helper">
                        <span>{t('Helper.FrozenUnavailable')}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BalanceHeader;
